import { FC, memo, useState, useEffect, useMemo } from "react";
import { CheckBox, SelectBox } from "devextreme-react";
import { LoadPanel } from "devextreme-react/load-panel";
import { useFetch } from "src/hooks/useFetch";
import { SpatialRun } from "../types";
import { decompressFloat32Array } from "src/utils/decompress";
import { tileLayoutManager, WellCount } from "./TileLayoutManager";
import { ColorRangeBar } from "src/components";
import { cycleProgressTileOrder } from "./cycle-progress-tile-order";
import { useRunTileLabels } from "./use-run-tile-labels";

// Response structure for tile-level data
interface TileDataResponse {
  runID: string;
  metric: string;
  numCycles: number;
  numTiles: number;
  numChannels?: number;
  numBases?: number;
  batches: string[];
  cycleOrder: number[];
  data: string; // gzip+base64 compressed
}

interface TilePosition {
  tileName: string;
  x: number;
  y: number;
  absoluteX: number;
  absoluteY: number;
  wellLocation: string;
  tileIndex: number;
}

interface CytoCycleTileProps {
  run: SpatialRun;
}

const metricOptions = [
  {
    value: "q30",
    label: "Q30",
    isPercentage: true,
    hasChannels: false,
    hasBases: false,
  },
  {
    value: "averageQScore",
    label: "Average Q Score",
    isPercentage: false,
    hasChannels: false,
    hasBases: false,
  },
  {
    value: "zScoreGreen",
    label: "Z Score Green",
    isPercentage: false,
    hasChannels: false,
    hasBases: false,
  },
  {
    value: "zScoreRed",
    label: "Z Score Red",
    isPercentage: false,
    hasChannels: false,
    hasBases: false,
  },
  {
    value: "baseComposition",
    label: "Base Composition",
    isPercentage: true,
    hasChannels: false,
    hasBases: true,
  },
  {
    value: "phasing",
    label: "Phasing",
    isPercentage: true,
    hasChannels: true,
    hasBases: false,
  },
  {
    value: "prephasing",
    label: "Prephasing",
    isPercentage: true,
    hasChannels: true,
    hasBases: false,
  },
  {
    value: "intensity",
    label: "Intensity",
    isPercentage: false,
    hasChannels: true,
    hasBases: false,
  },
  {
    value: "fwhm",
    label: "FWHM",
    isPercentage: false,
    hasChannels: true,
    hasBases: false,
  },
];

const channelList = [1, 2, 3, 4];
const baseList = ["A", "C", "G", "T"];

// Color scale
const altcolors = [
  "#ffffd9",
  "#edf8b1",
  "#c7e9b4",
  "#7fcdbb",
  "#41b6c4",
  "#1d91c0",
  "#225ea8",
  "#253494",
  "#081d58",
];

const getColor = (value: number, minv: number, maxv: number): string => {
  // Treat -999 as missing data
  if (isNaN(value) || value === -999 || value <= -900) return "#CCCCCC";
  const normalized = (value - minv) / (maxv - minv + 0.0001);
  if (normalized > 1) return "#800080";
  if (normalized < 0) return "#FFFF00";

  const idx = Math.min(
    Math.floor(normalized * (altcolors.length - 1)),
    altcolors.length - 2
  );
  const t = normalized * (altcolors.length - 1) - idx;
  return altcolors[Math.round(idx + t)];
};

const CytoCycleTile: FC<CytoCycleTileProps> = memo(
  ({ run }: CytoCycleTileProps) => {
    const [metric, setMetric] = useState("q30");
    const [range, setRange] = useState({ min: 0, max: 100 });
    const [defaultRange, setDefaultRange] = useState<{
      min: number;
      max: number;
    } | null>(null);
    const [fixScale, setFixScale] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState<string>("");
    const [selectedCycle, setSelectedCycle] = useState(1);
    const [channel, setChannel] = useState(1);
    const [base, setBase] = useState("A");
    const [zslice, setZslice] = useState(0);
    // TODO: Parse from recipeValues when format is known. Hardcoded for now.
    const numZSlices = 7;
    const [dataArray, setDataArray] = useState<number[]>([]);
    const [tilePositions, setTilePositions] = useState<TilePosition[]>([]);
    const {
      tileLabels,
      isLoading: isTileLabelsLoading,
      error: tileLabelsError,
    } = useRunTileLabels(run);
    const [tileValues, setTileValues] = useState<Map<string, number>>(
      new Map()
    );
    const [layoutLoaded, setLayoutLoaded] = useState(false);

    // Detect well count from run
    const wellCount: WellCount = useMemo(() => {
      return tileLayoutManager.detectWellCount(run?.wellLayout || "", run?.kitType);
    }, [run?.wellLayout, run?.kitType]);

    // Load tile layout
    useEffect(() => {
      const loadLayout = async () => {
        const positions = await tileLayoutManager.loadLayout(wellCount);
        setTilePositions(positions);
        setLayoutLoaded(true);
      };
      loadLayout();
    }, [wellCount]);

    const tileNameToIndexMap = useMemo(() => {
      const map = new Map<string, number>();
      tileLabels.forEach((tileName, idx) => {
        map.set(tileName, idx);
      });
      return map;
    }, [tileLabels]);

    const tilePositionByName = useMemo(() => {
      const map = new Map<string, TilePosition>();
      tilePositions.forEach((tile) => {
        map.set(tile.tileName, tile);
      });
      return map;
    }, [tilePositions]);

    // Fetch tile-level data
    const { data: rawData, isLoading } = useFetch(
      `/spatialRun/${run?.runID}/cycleProgress/${metric}/tiles`,
      !!run?.runID && !!metric
    );

    const tileResponse = rawData as TileDataResponse | undefined;

    const metricConfig = useMemo(
      () => metricOptions.find((m) => m.value === metric),
      [metric]
    );

    // Decompress the data when it arrives and initialize batch selection
    useEffect(() => {
      if (tileResponse?.data) {
        const decompressed = decompressFloat32Array(tileResponse.data);
        setDataArray(decompressed);
        // Initialize to first batch if not already set
        if (tileResponse.batches?.length > 0) {
          setSelectedBatch((prev) => prev || tileResponse.batches[0]);
        }
        setSelectedCycle(1);
      } else {
        setDataArray([]);
      }
    }, [tileResponse]);

    // Compute cycles per batch structure using run.batches which has cycle count per batch
    const batchCycleInfo = useMemo(() => {
      if (!tileResponse?.batches || !tileResponse?.cycleOrder) {
        return {
          batches: [] as string[],
          cyclesPerBatch: {} as Record<string, number[]>,
          cycleToIndex: {} as Record<string, number>,
        };
      }

      const { batches: apiBatches, cycleOrder } = tileResponse;

      // Build a map of batch -> cycle numbers using run.batches for cycle counts
      const cyclesPerBatch: Record<string, number[]> = {};
      const cycleToIndex: Record<string, number> = {};

      // Track the running index into cycleOrder
      let cycleOrderIdx = 0;

      apiBatches.forEach((batchName) => {
        // Find the batch info from run.batches to get the cycle count
        const batchInfo = run?.batches?.find((b) => b.Name === batchName);
        const numCyclesInBatch = batchInfo?.Cycles || 0;

        // Generate cycles 1 to numCyclesInBatch for this batch
        const cycles: number[] = [];
        for (let c = 1; c <= numCyclesInBatch; c++) {
          cycles.push(c);
          // Map batch-cycle to the index in cycleOrder where this data lives
          // Find the index in cycleOrder for this cycle
          const idxInOrder = cycleOrder.indexOf(c, cycleOrderIdx);
          if (idxInOrder !== -1) {
            cycleToIndex[`${batchName}-${c}`] = idxInOrder;
          }
        }

        // Move the starting search index for next batch
        cycleOrderIdx += numCyclesInBatch;

        cyclesPerBatch[batchName] = cycles;
      });

      return { batches: apiBatches, cyclesPerBatch, cycleToIndex };
    }, [tileResponse, run?.batches]);

    // Compute absolute cycleIdx from batch + cycle selection
    const cycleIdx = useMemo(() => {
      if (!selectedBatch || !selectedCycle) return 0;
      const key = `${selectedBatch}-${selectedCycle}`;
      return batchCycleInfo.cycleToIndex[key] ?? 0;
    }, [selectedBatch, selectedCycle, batchCycleInfo]);

    // Extract tile values for the current cycle/channel/base/zslice
    useEffect(() => {
      if (
        !dataArray ||
        dataArray.length === 0 ||
        !tileResponse ||
        tilePositions.length === 0
      ) {
        setTileValues(new Map());
        return;
      }

      const {
        numTiles: totalTilesPerCycle,
        numChannels = 0,
        numBases = 0,
      } = tileResponse;
      const hasChannels = metricConfig?.hasChannels || false;
      const hasBases = metricConfig?.hasBases || false;
      const isPercentage = metricConfig?.isPercentage || false;

      // Calculate actual number of tiles (totalTilesPerCycle = numTiles * numZSlices)
      const actualNumTiles = Math.floor(totalTilesPerCycle / numZSlices);

      // Data layout is interleaved: for each tile, all z-slices are together
      // [tile0_z0, tile0_z1, ..., tile0_z6, tile1_z0, tile1_z1, ..., tile1_z6, ...]
      // So to get value for tile T at z-slice Z: index = T * numZSlices + Z

      let cycleStart = 0;

      if (hasChannels && numChannels > 0) {
        const channelIdx = channel - 1;
        cycleStart =
          cycleIdx * numChannels * totalTilesPerCycle +
          channelIdx * totalTilesPerCycle;
      } else if (hasBases && numBases > 0) {
        const baseIdx = baseList.indexOf(base);
        cycleStart =
          cycleIdx * numBases * totalTilesPerCycle +
          baseIdx * totalTilesPerCycle;
      } else {
        cycleStart = cycleIdx * totalTilesPerCycle;
      }

      // Get the full tile data for this cycle (includes all z-slices interleaved)
      const allTileData = dataArray.slice(
        cycleStart,
        cycleStart + totalTilesPerCycle
      );

      // Extract values for the selected z-slice (interleaved format)
      const vals: number[] = [];
      for (let t = 0; t < actualNumTiles; t++) {
        const idx = t * numZSlices + zslice;
        vals.push(idx < allTileData.length ? allTileData[idx] : NaN);
      }

      // DEBUG: Log indices of non-NaN values in the raw data slice
      const nonNanIndicesInData: number[] = [];
      vals.forEach((v, i) => {
        if (!isNaN(v) && v > -900) {
          nonNanIndicesInData.push(i);
        }
      });
      console.log("=== Tile Data Debug ===");
      console.log(
        `Cycle start=${cycleStart}, actualNumTiles=${actualNumTiles}, numZSlices=${numZSlices}`
      );
      console.log(
        `Full data array for z-slice ${zslice} (extracted from interleaved):`,
        vals
      );
      console.log(
        `Non-NaN indices in data (${nonNanIndicesInData.length}):`,
        nonNanIndicesInData
      );
      console.log(
        `First few tile positions:`,
        tilePositions
          .slice(0, 5)
          .map((t) => ({ name: t.tileName, idx: t.tileIndex }))
      );

      // ZTile values are row-major (lane, row, column). RunParameters.Tiles is a
      // serpentine column walk, so sort the run's tiles into row-major order
      // before using them as indexes.
      const orderedTileNames = cycleProgressTileOrder(tileLabels);
      const valueMap = new Map<string, number>();
      let minv = Infinity;
      let maxv = -Infinity;

      if (tileNameToIndexMap.size === 0) {
        if (isTileLabelsLoading) {
          // Strict mode with no fallbacks: wait until runParameters tile labels finish loading.
          setTileValues(new Map());
          return;
        }
        console.error(
          "Missing runParameters tile labels after load. Refusing to map tile data without explicit tile ordering.",
          tileLabelsError
        );
        setTileValues(new Map());
        return;
      }

      if (vals.length < tileLabels.length) {
        console.error(
          `Tile data length (${vals.length}) is smaller than runParameters tile count (${tileLabels.length}). Refusing mapping.`
        );
        setTileValues(new Map());
        return;
      }

      // DEBUG: Track which tiles get non-NaN values
      const nonNanTiles: { name: string; idx: number; value: number }[] = [];

      // Every run tile must exist in the loaded layout.
      let hasRunLabelMissingFromLayout = false;
      orderedTileNames.forEach((tileName, tileDataIndex) => {
        const tilePos = tilePositionByName.get(tileName);
        if (!tilePos) {
          hasRunLabelMissingFromLayout = true;
          return;
        }
        if (tileDataIndex < vals.length) {
          let value = vals[tileDataIndex];
          if (isPercentage) value *= 100;
          valueMap.set(tileName, value);

          // Exclude -999 (missing data) from range calculation
          if (!isNaN(value) && value > -900) {
            if (value < minv) minv = value;
            if (value > maxv) maxv = value;
            nonNanTiles.push({
              name: tilePos.tileName,
              idx: tileDataIndex,
              value,
            });
          }
        }
      });

      if (hasRunLabelMissingFromLayout) {
        console.error(
          "Incomplete runParameters mapping: one or more runParameters tiles are missing from tile layout. Refusing partial mapping."
        );
        setTileValues(new Map());
        return;
      }

      console.log(
        `Non-NaN tiles displayed (${nonNanTiles.length}):`,
        nonNanTiles
      );
      console.log("=== End Debug ===");

      if (minv > maxv) {
        minv = 0;
        maxv = isPercentage ? 100 : 1;
      } else if (minv === maxv) {
        minv = minv - 0.1;
        maxv = maxv + 0.1;
      }

      setTileValues(valueMap);
      if (!fixScale) {
        setRange({ min: minv, max: maxv });
        setDefaultRange({ min: minv, max: maxv });
      }
    }, [
      dataArray,
      cycleIdx,
      channel,
      base,
      zslice,
      tileResponse,
      metricConfig,
      fixScale,
      tilePositions,
      tileLabels,
      tilePositionByName,
      tileNameToIndexMap,
      isTileLabelsLoading,
      tileLabelsError,
      numZSlices,
    ]);

    // Get available cycles for the selected batch
    const availableCycles = useMemo(() => {
      if (!selectedBatch || !batchCycleInfo.cyclesPerBatch[selectedBatch]) {
        return [];
      }
      return batchCycleInfo.cyclesPerBatch[selectedBatch];
    }, [selectedBatch, batchCycleInfo]);

    // Calculate display dimensions
    const SCALE = 12; // pixels per millimeter
    const TILE_WIDTH = 1.8 * SCALE;
    const TILE_HEIGHT = 2.6 * SCALE;

    // Get bounds from positions
    const bounds = useMemo(() => {
      if (tilePositions.length === 0)
        return { minX: 0, maxX: 100, minY: 0, maxY: 100 };
      const xs = tilePositions.map((t) => t.absoluteX);
      const ys = tilePositions.map((t) => t.absoluteY);
      return {
        minX: Math.min(...xs),
        maxX: Math.max(...xs),
        minY: Math.min(...ys),
        maxY: Math.max(...ys),
      };
    }, [tilePositions]);

    const containerWidth =
      (bounds.maxX - bounds.minX) * SCALE + TILE_WIDTH + 80;
    const containerHeight =
      (bounds.maxY - bounds.minY) * SCALE + TILE_HEIGHT + 80;

    return (
      <div style={{ marginTop: 20 }}>
        <LoadPanel
          shadingColor="rgba(0,0,0,0.4)"
          position={{ of: "#cyto-tile-heatmap" }}
          visible={isLoading || !layoutLoaded}
          showIndicator={true}
          shading={true}
        />

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 20,
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: 10 }}>Metric:</span>
            <SelectBox
              value={metric}
              items={metricOptions}
              valueExpr="value"
              displayExpr="label"
              onValueChanged={(e) => {
                setMetric(e.value);
                setSelectedCycle(1);
              }}
              width={180}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: 10 }}>Batch:</span>
            <SelectBox
              value={selectedBatch}
              items={batchCycleInfo.batches}
              onValueChanged={(e) => {
                setSelectedBatch(e.value);
                setSelectedCycle(1); // Reset to first cycle when batch changes
              }}
              width={100}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: 10 }}>Cycle:</span>
            <SelectBox
              value={selectedCycle}
              items={availableCycles}
              onValueChanged={(e) => setSelectedCycle(e.value)}
              width={80}
            />
          </div>

          {metricConfig?.hasChannels && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: 10 }}>Channel:</span>
              <SelectBox
                value={channel}
                items={channelList}
                onValueChanged={(e) => setChannel(e.value)}
                width={80}
              />
            </div>
          )}

          {metricConfig?.hasBases && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: 10 }}>Base:</span>
              <SelectBox
                value={base}
                items={baseList}
                onValueChanged={(e) => setBase(e.value)}
                width={80}
              />
            </div>
          )}

          {numZSlices > 1 && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: 10 }}>Z-Slice:</span>
              <SelectBox
                value={zslice}
                items={Array.from({ length: numZSlices }, (_, i) => i)}
                onValueChanged={(e) => setZslice(e.value)}
                width={80}
              />
            </div>
          )}
        </div>

        <div
          id="cyto-tile-heatmap"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 30,
          }}
        >
          {layoutLoaded && tilePositions.length > 0 && tileValues.size > 0 && (
            <>
              {/* Tile Heatmap */}
              <div
                style={{
                  position: "relative",
                  width: Math.min(containerWidth, 900),
                  height: Math.min(containerHeight, 700),
                  backgroundColor: "#f8f8f8",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  overflow: "auto",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: containerWidth - 40,
                    height: containerHeight - 40,
                    margin: 20,
                  }}
                >
                  {tilePositions.map((tile) => {
                    const value = tileValues.get(tile.tileName) ?? NaN;
                    const pixelX = (tile.absoluteX - bounds.minX) * SCALE;
                    const pixelY = (tile.absoluteY - bounds.minY) * SCALE;

                    return (
                      <div
                        key={tile.tileName}
                        style={{
                          position: "absolute",
                          left: pixelX,
                          top: pixelY,
                          width: TILE_WIDTH,
                          height: TILE_HEIGHT,
                          backgroundColor: getColor(
                            value,
                            range.min,
                            range.max
                          ),
                          border: "0.5px solid rgba(0,0,0,0.15)",
                          borderRadius: 1,
                          cursor: "pointer",
                        }}
                        title={`${tile.tileName} (${tile.wellLocation}): ${
                          isNaN(value) || value <= -900
                            ? "N/A"
                            : value.toFixed(2)
                        }${metricConfig?.isPercentage ? "%" : ""}`}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Color Scale */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ marginBottom: 5, fontSize: 12, color: "#666" }}>
                  {metricConfig?.label}
                </div>
                {defaultRange && (
                  <div
                    id="parentDiv"
                    style={{
                      width: 100,
                      height: Math.min(containerHeight - 40, 600),
                    }}
                  >
                    <ColorRangeBar
                      minv={defaultRange.min}
                      maxv={defaultRange.max}
                      rangeChanged={(r) => setRange(r)}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {layoutLoaded &&
            tilePositions.length > 0 &&
            tileValues.size === 0 &&
            !isLoading && (
              <div style={{ padding: 20, color: "#666" }}>
                No tile data available for this metric/cycle.
              </div>
            )}

          {layoutLoaded && tilePositions.length === 0 && (
            <div style={{ padding: 20, color: "#666" }}>
              Could not load tile layout for {wellCount} wells.
            </div>
          )}
        </div>

        <div style={{ marginTop: 20, display: "flex", gap: 20 }}>
          <CheckBox
            text="Fix Scale"
            value={fixScale}
            onValueChanged={(e) => setFixScale(e.value)}
          />
          <span style={{ color: "#666", fontSize: 12 }}>
            {tilePositions.length} tiles | {wellCount} well layout | Range:{" "}
            {range.min.toFixed(2)} - {range.max.toFixed(2)}
          </span>
        </div>
      </div>
    );
  }
);

export default CytoCycleTile;
